import React, {useEffect, useMemo} from 'react';
import {Box, Typography, IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {Image} from '@jobseeker/lotus/components/Image';
import {circularTick, Thunder, BadgeTick} from '@jobseeker/lotus/assets/icons';
import {getUbaPageName, TrackEventUBA} from '@jobseeker/common-core/utils';
import { FEATURE_LIST } from '@jobseeker/common-core/constants';

type FreeProBottomSheetProps = {
  onInstallClick: () => void;
  randomValue?: number;
};

const FreeBadge = ({fontSize}: {fontSize: number}) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      background: '#49966E',
      borderRadius: '4px',
      px: 0.625,
      py: 0,
      mx: 0.5,
      mt: -0.25,
      verticalAlign: 'middle',
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: fontSize,
        fontWeight: 600,
        color: '#fff',
        lineHeight: '1.2',
      }}
    >
      FREE
    </Typography>
  </Box>
);

type VariantConfig = {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  ctaLabel: string;
  index: number;
};

const VARIANTS: VariantConfig[] = [
  {
    title: 'Your 30-Day Pro Access Is Waiting',
    subtitle: (
      <>
        Install the app now to activate it for
        <FreeBadge fontSize={12} />
      </>
    ),
    ctaLabel: 'Install App to Activate',
    index: 0,
  },
  {
    title: (
      <>
        Get <FreeBadge fontSize={15} /> Pro Membership for 30 Days
      </>
    ),
    subtitle: 'Install the app to activate & unlock benefits',
    ctaLabel: 'Install App to Unlock Pro',
    index: 1,
  },
];

const FreeProBottomSheet: React.FC<FreeProBottomSheetProps> = ({
  onInstallClick,
  randomValue,
}) => {
  const variant = useMemo(
    () => VARIANTS[(randomValue ?? Math.random()) < 0.5 ? 0 : 1],
    []
  );
  const linkageName = `Install App On Listing pages`;
  const linkageType = 'Bottomsheet';
  const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME as 'iimjobs' | 'hirist';

  useEffect(() => {
    TrackEventUBA('appDownloadLinkageView', 'view', {
      platform: PLATFORM,
      linkageName,
      linkageType,

      pageName: getUbaPageName(),
      copy: variant.index + 1,
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  }, []);
  return (
    <Box
      sx={{
        borderRadius: '16px 16px 0 0',
        bgcolor: 'white',
        width: '100%',
        px: 2.5,
        pt: 3,
        pb: 4,
        mt: -3,
        position: 'relative',
      }}
    >
      {/* Title */}
      <Typography
        fontWeight={600}
        fontSize={15}
        color="text.primary"
        mb={0.25}
        component="div"
      >
        {variant.title}
      </Typography>

      {/* Subtitle */}
      <Typography
        fontSize={12}
        fontWeight={400}
        color="text.primary"
        mb={2.5}
        component="div"
      >
        {variant.subtitle}
      </Typography>

      {/* Features */}
      <Box display="flex" flexDirection="column" gap={1} mb={3}>
        {FEATURE_LIST.map(feature => (
          <Box key={feature} display="flex" alignItems="center" gap={1}>
            <Image
              src={circularTick}
              alt="tick"
              style={{height: 14, width: 14, flexShrink: 0}}
            />
            <Typography fontSize={12} fontWeight={400} color="#49966E">
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* CTA Button */}
      <Box
        onClick={() => {
          TrackEventUBA('appDownloadLinkageClick', 'click', {
            platform: PLATFORM,
            linkageName,
            linkageType,
            pageName: getUbaPageName(),
            copy: variant.index + 1,
            typeClick: 'CTA',
            sessionUsername: sessionStorage.getItem('userId') || '',
          });
          onInstallClick();
        }}
        sx={{
          bgcolor: '#49966E',
          borderRadius: '50px',
          py: 1.5,
          width: 'calc(100% + 32px)',
          textAlign: 'center',
          cursor: 'pointer',
          mb: 2,
        }}
      >
        <Typography fontSize={14} fontWeight={500} color="white">
          {variant.ctaLabel}
        </Typography>
      </Box>

      {/* Footer */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={2}
        width="calc(100% + 32px)"
      >
        <Box display="flex" alignItems="center" gap={0.25} ml={-1}>
          <Image
            src={Thunder}
            alt="lightning"
            style={{height: 18, width: 18}}
          />
          <Typography
            fontSize={10}
            fontWeight={400}
            color="rgba(51,51,51,0.60)"
          >
            Activates instantly on login
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Image src={BadgeTick} alt="shield" style={{height: 13, width: 13}} />
          <Typography
            fontSize={10}
            fontWeight={400}
            color="rgba(51,51,51,0.60)"
          >
            No card required
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FreeProBottomSheet;
